import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@voll-intelligence/domain';

import { authRepository } from '@web/data/repositories';
import { t } from '@web/i18n';

import { useSnackbar } from '@web/ui/core/molecules/snackbar';
import { fireAndForget } from '@web/ui/core/utils';

/**
 * Subscribes to the TanStack Query cache and translates HTTP error
 * shapes into uniform user-facing UX:
 *
 *   401 → redirect to Cognito login (session expired).
 *   403 → toast `error.forbidden`.
 *   404 → toast `error.feature_not_enabled` if the originating query was
 *         tagged `meta.kind === 'feature'`, otherwise route-local handling
 *         (we don't toast generic 404s — they belong to the component).
 *   409 → toast `error.conflict` with the server message appended.
 *   any other error inside an HttpError → `error.unexpected`.
 *
 * `meta.silent === true` opts a query/mutation out of every toast above —
 * the caller already renders its own error state — EXCEPT 401, which always
 * bounces through Cognito: the session expired regardless of who else
 * noticed.
 *
 * Mount once near the root of the app, INSIDE QueryClientProvider and
 * SnackbarProvider. Renders nothing — it's a side-effect-only sibling.
 */
export function GlobalErrorHandler() {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    function handle(rawError: unknown, meta?: Record<string, unknown>) {
      // Queries/mutations that render their own error state opt out of the
      // global toast. 401 is never silenced: the session bounce must happen
      // regardless of who noticed.
      if (meta?.silent === true && !(rawError instanceof HttpError && rawError.status === 401)) {
        return;
      }

      if (!(rawError instanceof HttpError)) {
        (globalThis as Partial<Pick<typeof globalThis, 'reportError'>>).reportError?.(rawError);
        showSnackbar({ severity: 'error', message: t('error.unexpected') });
        return;
      }

      switch (rawError.status) {
        case 401:
          showSnackbar({ severity: 'warning', message: t('error.unauthorized') });
          // Session expired — bounce back through Cognito.
          fireAndForget(
            authRepository.beginLogin({
              returnTo: globalThis.location.pathname + globalThis.location.search,
            }),
          );
          return;

        case 403:
          showSnackbar({ severity: 'error', message: t('error.forbidden') });
          return;

        case 404:
          if (meta?.kind === 'feature') {
            showSnackbar({
              severity: 'error',
              message: t('error.feature_not_enabled'),
            });
          }
          // Generic 404s: the route component decides what to render
          // (a "not found" view, an empty state, etc.). No global toast.
          return;

        case 409:
          showSnackbar({
            severity: 'error',
            message: `${t('error.conflict')} ${rawError.message}`.trim(),
          });
          return;

        default:
          (globalThis as Partial<Pick<typeof globalThis, 'reportError'>>).reportError?.(rawError);
          showSnackbar({ severity: 'error', message: t('error.unexpected') });
      }
    }

    const unsubQuery = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handle(event.action.error, event.query.options.meta);
      }
    });

    const unsubMutation = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handle(event.action.error, event.mutation.options.meta);
      }
    });

    return () => {
      unsubQuery();
      unsubMutation();
    };
  }, [queryClient, showSnackbar]);

  return null;
}
